import { useTranslation } from 'react-i18next'
import { useSearchParams, useNavigate } from 'react-router'
import type {
  PbFindProcessesRequest,
  PbFindProcessesResponse,
  PbPublicReturnProcess
} from '@itsrever/rever-api'
import { useDebounce } from '@/hooks/use-debounce'
import { useEcommerceFilter } from '@/hooks/use-selected-ecommerce-ids'
import { useProcesses } from '@/hooks/use-processes-v2'
import { ReturnStatus } from '@/utils'
import {
  ALL_ORDERS,
  processesRequestFrom
} from '@/domain/processes/list-request'
import { ListTabs, useListTabsPage } from '@/ui/shared/list-tabs'
import { columns } from './processes-columns'

export const REVIEW_REQUIRED = ReturnStatus.ReviewRequired
const AUTHORIZATION_PENDING = ReturnStatus.AuthorizationPending

const DEFAULT_PAGE_LIMIT = 25

export const ProcessesTabs = () => {
  const { t } = useTranslation()

  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()
  const { offsetFor } = useListTabsPage(ALL_ORDERS, DEFAULT_PAGE_LIMIT)

  const ecommerceId = useEcommerceFilter()

  const debouncedSearch = useDebounce(
    searchParams.get('search') ?? undefined,
    300
  )

  const filterParams: PbFindProcessesRequest = {
    ...processesRequestFrom(searchParams, {
      ecommerceId,
      freetext: debouncedSearch
    }),
    order: 'DESC',
    limit: DEFAULT_PAGE_LIMIT
  }

  const reviewRequired = useProcesses({
    ...filterParams,
    order: 'ASC',
    return_status: REVIEW_REQUIRED,
    offset: offsetFor(REVIEW_REQUIRED)
  })

  const authorizationPending = useProcesses({
    ...filterParams,
    order: 'ASC',
    return_status: AUTHORIZATION_PENDING,
    offset: offsetFor(AUTHORIZATION_PENDING)
  })

  const all = useProcesses({
    ...filterParams,
    return_status: undefined,
    offset: offsetFor(ALL_ORDERS)
  })

  const onRowClick = (data: PbPublicReturnProcess) => {
    searchParams.set('ecommerce_id', data.ecommerce_id ?? '')
    setSearchParams(searchParams)

    navigate({
      pathname: `/returns/orders/${data.process_id}`,
      search: searchParams.toString()
    })
  }

  return (
    <ListTabs
      tabs={[
        {
          ...tabFrom(authorizationPending),
          value: AUTHORIZATION_PENDING,
          label: t('topbar_processes.authorization_pending'),
          hidden: !authorizationPending.data?.processes?.length
        },
        {
          ...tabFrom(reviewRequired),
          value: REVIEW_REQUIRED,
          label: t('topbar_processes.review_required'),
          hidden: !reviewRequired.data?.processes?.length
        },
        {
          ...tabFrom(all),
          value: ALL_ORDERS,
          label: t('topbar_processes.all')
        }
      ]}
      allTab={ALL_ORDERS}
      pageSize={DEFAULT_PAGE_LIMIT}
      columns={columns}
      onRowClick={onRowClick}
      pinFirstColumn
    />
  )
}

const tabFrom = ({
  data,
  isPending,
  isFetching
}: {
  data?: PbFindProcessesResponse
  isPending: boolean
  isFetching: boolean
}) => ({
  rows: data && (data.processes ?? []),
  count: Number(data?.rowcount ?? 0),
  isPending,
  isFetching
})
